<script setup lang="ts">
    import { computed } from 'vue';
    import { RouteLocationRaw, RouterLink } from 'vue-router';
    import EchoTooltip from './EchoTooltip.vue';

    defineOptions({
        inheritAttrs: false,
    });

    const props = withDefaults(
        defineProps<{
            type?: 'button' | 'submit';
            disabled?: boolean;
            size?: `size-${number}`;
            areaSize?:
                | `size-${number}`
                | `w-${number | 'full'} h-${number}`
                | `h-${number} w-${number | 'full'}`;
            ariaLabel?: string;
            tooltip?: string;
            route?: RouteLocationRaw;
        }>(),
        {
            type: 'button',
            route: undefined,
        }
    );

    const buttonSize = computed(() => {
        if (props.areaSize) {
            return props.areaSize;
        }

        if (props.size) {
            return props.size;
        }

        return undefined;
    });

    // A disabled link can't be blocked from navigating, so it falls back to a disabled button.
    const isLink = computed(() => !!props.route && !props.disabled);

    const element = computed(() => (isLink.value ? RouterLink : 'button'));

    const elementBindings = computed(() =>
        isLink.value ? { to: props.route } : { type: props.type, disabled: props.disabled }
    );
</script>

<template>
    <EchoTooltip v-if="props.tooltip" :label="props.tooltip" :disabled="props.disabled">
        <component
            :is="element"
            v-bind="{ ...$attrs, ...elementBindings }"
            class="Icon-button"
            :class="buttonSize"
            :aria-label="props.ariaLabel ?? props.tooltip"
        >
            <div class="relative flex items-center justify-center" :class="props.size">
                <slot />
            </div>
        </component>
    </EchoTooltip>
    <component
        :is="element"
        v-else
        v-bind="{ ...$attrs, ...elementBindings }"
        class="Icon-button"
        :class="buttonSize"
        :aria-label="props.ariaLabel"
    >
        <div class="relative flex items-center justify-center" :class="props.size">
            <slot />
        </div>
    </component>
</template>
